// Item catalog. All player-facing text is ENGLISH.
// Grid sizes are DayZ-style inventory cells [w, h].
// Effects: food/water restore hunger/thirst (0-100 scales); health heals;
// poison deals damage over time (delayMin = real-world-inspired onset).
//
// Mushroom facts are grounded in reality:
//  - Porcini / chanterelle / birch bolete: choice edibles.
//  - Morel & false morel: TOXIC RAW (gyromitrin / hydrazine compounds).
//  - Fly agaric: muscimol — nausea, delirium; rarely lethal.
//  - Death cap: amatoxin — mild at first, organ failure after a delay. Deadly.
//  - Elderberry & rowan: mildly toxic raw (cyanogenic glycosides / parasorbic
//    acid); fine cooked.

export const ITEMS = {
  // ---- backpacks (worn on back; grid = their carry space) ----
  backpack_courier: {
    name: 'Courier Bag', kind: 'backpack', size: [3, 3], grid: [4, 3],
    desc: 'A worn canvas shoulder bag. Better than pockets.',
  },
  backpack_hiking: {
    name: 'Hiking Backpack', kind: 'backpack', size: [4, 4], grid: [5, 5],
    desc: 'A solid Soviet-era hiking pack with external straps.',
  },
  backpack_expedition: {
    name: 'Expedition Rucksack', kind: 'backpack', size: [5, 5], grid: [6, 7],
    desc: 'A huge expedition frame pack. Swallows everything.',
  },

  // ---- clothing (grid = pockets; quickslots add to the quickbar) ----
  shorts: {
    name: 'Shorts', kind: 'pants', size: [2, 2], grid: [1, 2], quickslots: 0,
    desc: 'Frayed shorts. Two shallow pockets, no warmth.',
  },
  work_pants: {
    name: 'Work Pants', kind: 'pants', size: [2, 3], grid: [2, 3], quickslots: 1,
    desc: 'Sturdy canvas work pants with deep pockets.',
  },
  tshirt: {
    name: 'T-Shirt', kind: 'shirt', size: [2, 2], grid: [1, 1], quickslots: 0,
    desc: 'A faded t-shirt. Holds almost nothing.',
  },
  flannel_shirt: {
    name: 'Flannel Shirt', kind: 'shirt', size: [2, 2], grid: [2, 2], quickslots: 1,
    desc: 'A thick flannel shirt with chest pockets.',
  },
  rain_jacket: {
    name: 'Rain Jacket', kind: 'jacket', size: [2, 3], grid: [2, 2], quickslots: 1,
    desc: 'A crinkly rain jacket. Keeps you dry, carries a little.',
  },
  field_jacket: {
    name: 'Field Jacket', kind: 'jacket', size: [3, 3], grid: [3, 3], quickslots: 2,
    desc: 'A military field jacket. Pockets everywhere.',
  },

  // ---- mushrooms (found at tree bases in forests) ----
  mush_porcini: {
    name: 'Porcini', kind: 'food', size: [1, 1], stack: 4,
    eat: { food: 22, water: 4 },
    desc: 'King bolete — a prized, meaty edible.',
  },
  mush_chanterelle: {
    name: 'Chanterelle', kind: 'food', size: [1, 1], stack: 4,
    eat: { food: 14, water: 4 },
    desc: 'Golden, apricot-scented. Reliable and tasty.',
  },
  mush_birch_bolete: {
    name: 'Birch Bolete', kind: 'food', size: [1, 1], stack: 4,
    eat: { food: 16, water: 5 },
    desc: 'Brown-capped bolete that grows under birches.',
  },
  mush_puffball: {
    name: 'Puffball', kind: 'food', size: [1, 1], stack: 4,
    eat: { food: 12, water: 8 },
    desc: 'Edible while the flesh is still pure white.',
  },
  mush_morel: {
    name: 'Morel', kind: 'food', size: [1, 1], stack: 4,
    eat: { food: 18, water: 3, poison: { dmg: 8, delay: 30 } },
    cookedId: 'mush_morel_roasted',
    desc: 'Honeycomb cap. Delicious cooked — mildly toxic raw.',
  },
  mush_morel_roasted: {
    name: 'Roasted Morel', kind: 'food', size: [1, 1], stack: 4,
    eat: { food: 24, water: 2 },
    desc: 'Roasted until the toxins cooked off. Excellent.',
  },
  mush_fly_agaric: {
    name: 'Fly Agaric', kind: 'food', size: [1, 1], stack: 4,
    eat: { food: 6, water: 2, poison: { dmg: 22, delay: 20 } },
    desc: 'Red cap, white spots. The fairy-tale one. Do not.',
  },
  mush_death_cap: {
    name: 'Death Cap', kind: 'food', size: [1, 1], stack: 4,
    eat: { food: 10, water: 2, poison: { dmg: 90, delay: 60 } },
    desc: 'Pale green-white, innocent-looking. Amatoxin: feels fine at first. It is not fine.',
  },
  mush_false_morel: {
    name: 'False Morel', kind: 'food', size: [1, 1], stack: 4,
    eat: { food: 12, water: 2, poison: { dmg: 35, delay: 40 } },
    cookedId: 'mush_false_morel_roasted',
    desc: 'Brain-like folds. Gyromitrin — toxic, even cooking cannot be trusted.',
  },
  mush_false_morel_roasted: {
    name: 'Roasted False Morel', kind: 'food', size: [1, 1], stack: 4,
    eat: { food: 16, water: 1, poison: { dmg: 8, delay: 30 } },
    desc: 'Cooking drove off most of the gyromitrin. Most.',
  },

  // ---- berries & fruit (special bushes and trees) ----
  berry_blueberry: {
    name: 'Blueberries', kind: 'food', size: [1, 1], stack: 6,
    eat: { food: 6, water: 8 },
    desc: 'A handful of forest blueberries.',
  },
  berry_raspberry: {
    name: 'Raspberries', kind: 'food', size: [1, 1], stack: 6,
    eat: { food: 7, water: 7 },
    desc: 'Wild raspberries from a bramble.',
  },
  berry_lingonberry: {
    name: 'Lingonberries', kind: 'food', size: [1, 1], stack: 6,
    eat: { food: 5, water: 6 },
    desc: 'Tart red lingonberries. Keep well.',
  },
  berry_elderberry: {
    name: 'Elderberries', kind: 'food', size: [1, 1], stack: 6,
    eat: { food: 5, water: 6, poison: { dmg: 6, delay: 25 } },
    cookedId: 'berry_elderberry_cooked',
    desc: 'Dark clusters. Slightly toxic raw — cook them.',
  },
  berry_elderberry_cooked: {
    name: 'Stewed Elderberries', kind: 'food', size: [1, 1], stack: 6,
    eat: { food: 9, water: 4 },
    desc: 'Heat breaks down the glycosides. Sweet and safe.',
  },
  berry_rosehip: {
    name: 'Rosehips', kind: 'food', size: [1, 1], stack: 6,
    eat: { food: 6, water: 5, health: 3 },
    desc: 'Bright red hips, full of vitamin C.',
  },
  fruit_apple: {
    name: 'Apple', kind: 'food', size: [1, 1], stack: 4,
    eat: { food: 12, water: 12 },
    desc: 'A small wild apple. Crisp and sour.',
  },
  berry_rowan: {
    name: 'Rowan Berries', kind: 'food', size: [1, 1], stack: 6,
    eat: { food: 4, water: 4, poison: { dmg: 5, delay: 20 } },
    cookedId: 'berry_rowan_cooked',
    desc: 'Bitter orange berries. Raw they bite back a little.',
  },
  berry_rowan_cooked: {
    name: 'Stewed Rowan', kind: 'food', size: [1, 1], stack: 6,
    eat: { food: 8, water: 3 },
    desc: 'Cooked down to a tart, harmless mash.',
  },

  // ---- meat & cooking (Phase: hunting) ----
  meat_raw_chicken: {
    name: 'Raw Chicken', kind: 'food', size: [1, 1], stack: 3,
    eat: { food: 8, poison: { dmg: 18, delay: 25 } },
    cookedId: 'meat_cooked_chicken',
    desc: 'A plucked chicken breast. Cook it.',
  },
  meat_cooked_chicken: {
    name: 'Roast Chicken', kind: 'food', size: [1, 1], stack: 3,
    eat: { food: 32 },
    desc: 'Roasted over a fire. Smells wonderful.',
  },
  meat_raw_mutton: {
    name: 'Raw Mutton', kind: 'food', size: [2, 1], stack: 3,
    eat: { food: 10, poison: { dmg: 14, delay: 25 } },
    cookedId: 'meat_cooked_mutton',
    desc: 'A cut of sheep or goat. Needs fire.',
  },
  meat_cooked_mutton: {
    name: 'Roast Mutton', kind: 'food', size: [2, 1], stack: 3,
    eat: { food: 40 },
    desc: 'Rich roast meat on the bone.',
  },
  meat_raw_game: {
    name: 'Raw Game Meat', kind: 'food', size: [2, 1], stack: 3,
    eat: { food: 9, poison: { dmg: 16, delay: 25 } },
    cookedId: 'meat_cooked_game',
    desc: 'Lean, dark predator meat. Cook it well.',
  },
  meat_cooked_game: {
    name: 'Roast Game Meat', kind: 'food', size: [2, 1], stack: 3,
    eat: { food: 34 },
    desc: 'Gamey but filling. A hunter’s trophy dinner.',
  },
  // ---- scavenged food & drink (loot spawner; mapped from loot-tables.json) ----
  canned_stew: {
    name: 'Canned Stew', kind: 'food', size: [1, 1], stack: 2,
    eat: { food: 35, water: 6 },
    desc: 'Army-issue beef stew. Dented but sound.',
  },
  canned_fish: {
    name: 'Canned Fish', kind: 'food', size: [1, 1], stack: 2,
    eat: { food: 28, water: 4 },
    desc: 'Sprats in oil. The apocalypse staple.',
  },
  canned_veg: {
    name: 'Canned Vegetables', kind: 'food', size: [1, 1], stack: 2,
    eat: { food: 20, water: 8 },
    desc: 'Peas or asparagus — the label is gone.',
  },
  canned_soup: {
    name: 'Canned Soup', kind: 'food', size: [1, 1], stack: 2,
    eat: { food: 22, water: 12 },
    desc: 'Condensed soup. Fine cold, better warm.',
  },
  canned_fruit: {
    name: 'Canned Fruit', kind: 'food', size: [1, 1], stack: 2,
    eat: { food: 16, water: 14 },
    desc: 'Fruit cocktail in syrup. A small luxury.',
  },
  bulging_can: {
    name: 'Bulging Can', kind: 'food', size: [1, 1], stack: 2,
    eat: { food: 30, water: 5, poison: { dmg: 45, delay: 50 } },
    desc: 'The lid is domed outward. Botulism does that.',
  },
  candy_bar: {
    name: 'Candy Bar', kind: 'food', size: [1, 1], stack: 4,
    eat: { food: 12 },
    desc: 'Caramel, mostly melted and reset a few times.',
  },
  crackers: {
    name: 'Crackers', kind: 'food', size: [1, 1], stack: 4,
    eat: { food: 14 },
    desc: 'A dry sleeve of crackers. They keep forever.',
  },
  soda_can: {
    name: 'Soda Can', kind: 'drink', size: [1, 1], stack: 4,
    eat: { water: 22, food: 5 },
    desc: 'Kola. Flat, warm, wonderful.',
  },
  juice_can: {
    name: 'Juice Can', kind: 'drink', size: [1, 1], stack: 4,
    eat: { water: 18, food: 7 },
    desc: 'Pineapple juice from another decade.',
  },
  vodka_bottle: {
    name: 'Vodka', kind: 'drink', size: [1, 2], stack: 1,
    eat: { water: 6, food: 2, health: 2 },
    desc: 'Cheap vodka. Steadies the hands, briefly.',
  },
  murky_water: {
    name: 'Murky Water', kind: 'drink', size: [1, 2], stack: 1,
    eat: { water: 30, poison: { dmg: 10, delay: 35 } },
    desc: 'A bottle of grey water. Desperation only.',
  },

  // ---- medical (loot) ----
  bandage: {
    name: 'Bandage', kind: 'med', size: [1, 1], stack: 3, patch: true,
    eat: { health: 8 },
    desc: 'A sealed cloth bandage. Stops bleeding, cleans the wound.',
  },
  duct_tape: {
    name: 'Duct Tape', kind: 'med', size: [1, 1], stack: 3, patch: true,
    desc: 'Silver tape. Not sterile, but it stops the bleeding.',
  },
  painkillers: {
    name: 'Painkillers', kind: 'med', size: [1, 1], stack: 3,
    eat: { health: 8 }, suppress: 'symptoms',
    desc: 'A bottle of analgesic tablets. Quiets a fever — cures nothing.',
  },
  antibiotics: {
    name: 'Antibiotics', kind: 'med', size: [1, 1], stack: 2,
    eat: { health: 25 }, cure: 'infection',
    desc: 'Ciprofloxacin, still in date. The only cure for an infected wound.',
  },
  // stripped off an infected: cloth you can press into a wound, at a price
  rags: {
    name: 'Filthy Rags', kind: 'med', size: [1, 1], stack: 4, patch: true,
    eat: { health: 1 }, infectRisk: 0.25,
    desc: 'Torn clothing off a corpse. Stops the bleeding. It is not clean.',
  },

  purification_tablets: {
    name: 'Purification Tablets', kind: 'tool', size: [1, 1], stack: 6,
    desc: 'Chlorine tablets. One makes any water safe to drink.',
  },

  // ---- flavor valuables (loot; trade value later) ----
  cigarettes: {
    name: 'Cigarettes', kind: 'valuable', size: [1, 1], stack: 4,
    desc: 'A pack of cigarettes. Currency out here.',
  },
  pocket_watch: {
    name: 'Wristwatch', kind: 'valuable', size: [1, 1], stack: 1,
    desc: 'Still ticking. Somebody wound it once a day.',
  },
  wallet: {
    name: 'Wallet', kind: 'valuable', size: [1, 1], stack: 2,
    desc: 'Rubles, a bus pass, a photo of somebody. Worthless and not.',
  },

  // ---- ammunition (rare corpse/military find; magazines come later) ----
  ammo_pistol: {
    name: 'Pistol Rounds', kind: 'tool', size: [1, 1], stack: 6,
    desc: '9×18 mm Makarov, loose in a paper sleeve.',
  },
  ammo_rifle: {
    name: 'Rifle Rounds', kind: 'tool', size: [1, 1], stack: 6,
    desc: '7.62×39 mm on a stripper clip. Somebody was ready for this.',
  },

  cooking_stick: {
    name: 'Cooking Stick', kind: 'tool', size: [1, 3], stack: 1,
    desc: 'A whittled green stick for roasting over a fire.',
  },
  matches: {
    name: 'Matches', kind: 'tool', size: [1, 1], stack: 1,
    desc: 'A box of hunting matches. Lights a campfire.',
  },
  water_bottle: {
    name: 'Water Bottle', kind: 'drink', size: [1, 2], stack: 1,
    eat: { water: 45 },
    desc: 'A dented aluminium bottle of water.',
  },
};

export function itemDef(id) { return ITEMS[id]; }
